/**
 * Shared transform utilities for the Models module.
 *
 * These functions handle conversion between the UI model builder format and
 * the API wire format, as well as helpers for market stability indicators.
 * Centralised here so ModelPage and ModelsOverview share identical logic.
 */

import { STABILITY_BARS } from '../constants/models';

// ---------------------------------------------------------------------------
// Builder → API transforms
// ---------------------------------------------------------------------------

/**
 * Parse a referenced product ID from multiple possible field names.
 * Returns null when the value cannot be parsed as a valid integer.
 *
 * @param {Object} subProduct - Sub-product object from the builder
 * @returns {number|null}
 */
export const getReferencedProductId = (subProduct) => {
  const referenceId = subProduct.productRefId ?? subProduct.product_ref_id ?? subProduct.id;
  const parsed = Number.parseInt(referenceId, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Returns true when any sub-product has nested material components (not a locked reference).
 * Used to decide whether to call the /nested endpoint.
 *
 * @param {Object} modelData - Model data from the builder overlay
 * @returns {boolean}
 */
export const hasNestedSubProducts = (modelData) =>
  modelData.subProducts?.some(sp => !sp.locked && sp.components?.length > 0) ?? false;

/**
 * Transform UI model data (from ModelBuilderOverlay) into the API request payload.
 * Handles three component types:
 *   - Standalone material components
 *   - Locked product references (existing products)
 *   - Inline sub-products with nested material components
 *
 * @param {Object} modelData - Data from ModelBuilderOverlay
 * @param {string} [defaultName='Model'] - Fallback name when modelData.name is empty
 * @returns {Object} API-formatted payload for POST/PUT /products or /products/nested
 */
export const transformModelDataToApiFormat = (modelData, defaultName = 'Model') => {
  const apiComponents = [];
  let sortOrder = 1;

  // Standalone material components
  (modelData.components ?? []).forEach(comp => {
    apiComponents.push({
      component_type: 'material',
      material_id: comp.id ? parseInt(comp.id, 10) : null,
      name: comp.name,
      quantity: comp.amount || 1,
      unit: comp.unit || 'kg',
      currency: comp.currency || modelData.currency || 'USD',
      source_country: comp.source || null,
      destination_country: comp.destination || null,
      sort_order: sortOrder++,
    });
  });

  // Sub-products: either a locked product reference or an inline group with nested materials
  (modelData.subProducts ?? []).forEach(subProd => {
    if (subProd.type === 'product' && subProd.locked) {
      // Reference to an existing product
      apiComponents.push({
        component_type: 'product',
        product_ref_id: getReferencedProductId(subProd),
        name: subProd.name,
        quantity: subProd.amount || 1,
        unit: subProd.unit || 'unit',
        currency: subProd.currency || modelData.currency || 'USD',
        sort_order: sortOrder++,
      });
    } else {
      // Inline sub-product with nested materials
      const nestedComponents = (subProd.components ?? []).map((comp, idx) => ({
        component_type: 'material',
        material_id: comp.id ? parseInt(comp.id, 10) : null,
        name: comp.name,
        quantity: comp.amount || 1,
        unit: comp.unit || 'kg',
        currency: comp.currency || modelData.currency || 'USD',
        source_country: comp.source || null,
        destination_country: comp.destination || null,
        sort_order: idx + 1,
      }));

      apiComponents.push({
        component_type: 'product',
        name: subProd.name,
        quantity: subProd.amount || 1,
        unit: subProd.unit || 'unit',
        currency: subProd.currency || modelData.currency || 'USD',
        sort_order: sortOrder++,
        sub_product: {
          name: subProd.name,
          description: `Sub-product of ${modelData.name || defaultName}`,
          components: nestedComponents,
        },
      });
    }
  });

  return {
    name: modelData.name || defaultName,
    description: modelData.description || '',
    default_currency: modelData.currency || 'USD',
    base_unit: modelData.unit || 'unit',
    base_quantity: modelData.totalAmount || 1,
    components: apiComponents,
  };
};

// ---------------------------------------------------------------------------
// Component traversal helpers
// ---------------------------------------------------------------------------

/**
 * Resolve a sub-product's detail object from either the server-fetched details map
 * (for product references) or the inline sub_product field (for inline sub-products).
 *
 * @param {Object} productComponent - A component with component_type === 'product'
 * @param {Object} subProductDetails - Map of { [product_ref_id]: detailObject }
 * @returns {Object|undefined}
 */
export const getSubProductDetail = (productComponent, subProductDetails) => {
  if (productComponent.product_ref_id) {
    return subProductDetails[productComponent.product_ref_id];
  }
  return productComponent.sub_product;
};

/**
 * Collect all material components nested inside product-type components.
 *
 * @param {Array} productComponents - Components with component_type === 'product'
 * @param {Object} subProductDetails - Map of { [product_ref_id]: detailObject }
 * @returns {Array} Flat list of material components
 */
export const getNestedMaterialComponents = (productComponents, subProductDetails) =>
  productComponents.flatMap(pc => {
    const detail = getSubProductDetail(pc, subProductDetails);
    return detail?.components?.filter(c => c.component_type === 'material') ?? [];
  });

// ---------------------------------------------------------------------------
// Market stability helpers
// ---------------------------------------------------------------------------

/** @param {string} country */
const normalizeCountryName = (country) => (country ?? '').trim().toLowerCase();

/**
 * Convert a stability percentage to a 3-level label.
 *
 * @param {number} percentage - 0–100
 * @returns {'high'|'medium'|'low'}
 */
export const getStabilityLevel = (percentage) => {
  if (percentage >= 70) return 'high';
  if (percentage >= 40) return 'medium';
  return 'low';
};

/**
 * Build a fixed-length array of bar objects for the stability indicator widget.
 *
 * @param {number} percentage - 0–100
 * @param {number} [totalBars=STABILITY_BARS]
 * @returns {Array<{ filled: boolean }>}
 */
export const buildStabilityBars = (percentage, totalBars = STABILITY_BARS) => {
  const filled = Math.max(0, Math.min(totalBars, Math.round((percentage / 100) * totalBars)));
  return Array.from({ length: totalBars }, (_, i) => ({ filled: i < filled }));
};

/**
 * Find a matching market-stability entry for a given source country and convert it
 * into the indicator shape expected by ModelBreakdownTable.
 *
 * @param {Array|null} marketStabilityEntries - Array of { country, stability } objects
 * @param {string|null} sourceCountry
 * @returns {{ level: string, percentage: number, bars: Array }|null}
 */
export const mapMarketStabilityToIndicator = (marketStabilityEntries, sourceCountry) => {
  if (!Array.isArray(marketStabilityEntries) || marketStabilityEntries.length === 0) return null;
  if (!sourceCountry) return null;

  const match = marketStabilityEntries.find(
    e => normalizeCountryName(e.country) === normalizeCountryName(sourceCountry)
  );

  if (typeof match?.stability !== 'number') return null;

  return {
    level: getStabilityLevel(match.stability),
    percentage: match.stability,
    bars: buildStabilityBars(match.stability),
  };
};
